import { useEffect } from "react";
import { AddItemForm } from "./components/AddItemForm";
import { ListItems } from "./components/ListItems";
import { SelectAllRow } from "./components/SelectAllRow";
import { ListSelect } from "./components/ListSelect";
import { NewListButton } from "./components/NewListButton";
import { useListsContext } from "./ListsContext";
import {
  DismissButton,
  EmptyState,
  ErrorBanner,
  ListsNotice,
  ListsPageWrapper,
  PendingNotice,
  SyncedNotice,
  PanelCard,
  PanelSection,
  PanelActions,
  PanelHeader,
  SmallButton,
} from "./Lists.styled";

export default function ListsPage() {
  const {
    lists,
    itemsByList,
    selectedListId,
    loadState,
    errorMessage,
    demoMode,
    resetDemoData,
    pendingCount,
    syncedCount,
    clearSyncedCount,
    usingCachedData,
    clearError,
    loadLists,
  } = useListsContext();

  // Confirmation that queued work went through is worth seeing, but not worth keeping
  // on screen once it has been read.
  useEffect(() => {
    if (syncedCount == null) {
      return;
    }
    const timer = setTimeout(clearSyncedCount, 4000);
    return () => clearTimeout(timer);
  }, [syncedCount, clearSyncedCount]);

  const loggedIn = localStorage.getItem("CognitoIdToken") != null;
  // Demo mode needs no session, so the sample lists are visible either way.
  const showLists = loggedIn || demoMode;

  // The only place lists are ever fetched — on this page's mount, never at login.
  // loadLists guards itself so navigating away and back does not refetch.
  useEffect(() => {
    if (showLists) {
      loadLists();
    }
  }, [showLists, loadLists]);

  const selectedList = lists.find((list) => list.ListId === selectedListId);
  const selectedItems = selectedListId
    ? itemsByList[selectedListId] ?? []
    : [];

  return (
    <ListsPageWrapper>
      {!showLists && <ListsNotice>You are not logged in</ListsNotice>}

      {demoMode && (
        <ListsNotice>
          <strong>Sample data.</strong> The lists API isn't connected yet, so these
          lists are local to this browser and nothing is saved to your account.
          <br />
          <SmallButton onClick={resetDemoData}>Reset sample data</SmallButton>
        </ListsNotice>
      )}

      {showLists && (
        <>
          {/* Offline state. Worth saying plainly, because the change the user just made
              looks saved and they should know it has not reached the server yet. */}
          {pendingCount > 0 && (
            <PendingNotice>
              {pendingCount} change{pendingCount !== 1 ? "s" : ""} saved on this
              device, waiting for a connection.
            </PendingNotice>
          )}

          {syncedCount != null && (
            <SyncedNotice>
              {syncedCount} change{syncedCount !== 1 ? "s" : ""} synced.
            </SyncedNotice>
          )}

          {usingCachedData && (
            <ListsNotice>
              Couldn't reach the server, so this is the copy saved on this device. Your
              changes are still being kept and will sync when you're back online.
            </ListsNotice>
          )}

          {errorMessage != null && (
            <ErrorBanner>
              <span>{errorMessage}</span>
              <DismissButton aria-label="Dismiss error" onClick={clearError}>
                ×
              </DismissButton>
            </ErrorBanner>
          )}

          {loadState === "loading" && <EmptyState>Loading your lists…</EmptyState>}

          {loadState === "error" && (
            <SmallButton onClick={() => loadLists(true)}>Try again</SmallButton>
          )}

          {loadState === "ready" && (
            <>
              {/* No panel to hang the selector off yet, so the create button
                  stands on its own here. */}
              {lists.length === 0 && (
                <EmptyState>
                  You don't have any lists yet. Create one to get started.
                  <br />
                  <br />
                  <NewListButton />
                </EmptyState>
              )}

              {/* Three sections of one card: choose the list, work through it,
                  add to it. The thick rules between them match the card border. */}
              {selectedList != null && (
                <PanelCard>
                  <PanelSection>
                    {/* The dropdown is the list's name — there is no separate title. */}
                    <PanelHeader>
                      <ListSelect />
                      <PanelActions>
                        <NewListButton />
                      </PanelActions>
                    </PanelHeader>
                  </PanelSection>

                  <PanelSection>
                    {selectedItems.length === 0 ? (
                      <EmptyState>This list is empty.</EmptyState>
                    ) : (
                      <>
                        <SelectAllRow
                          listId={selectedList.ListId}
                          items={selectedItems}
                        />
                        <ListItems
                          listId={selectedList.ListId}
                          items={selectedItems}
                        />
                      </>
                    )}
                  </PanelSection>

                  <PanelSection>
                    <AddItemForm listId={selectedList.ListId} />
                  </PanelSection>
                </PanelCard>
              )}
            </>
          )}
        </>
      )}
    </ListsPageWrapper>
  );
}
